/**
 * GalleryManager - Manages gallery state and media data
 *
 * @module GalleryManager
 */

'use strict';

/**
 * Gallery Manager Class
 */
export class GalleryManager {
    /**
     * Create gallery manager instance
     * @param {MediaConfig} config - Plugin configuration
     */
    constructor(config) {
        this.config = config;
        this.galleries = {};
        this.currentGallery = null;
        this.currentIndex = 0;
        this.mediaElements = [];
    }

    /**
     * Collect all media elements from DOM
     * @param {string} selector - CSS selector
     * @returns {Array<MediaData>} Array of media data objects
     */
    collectMediaElements(selector) {
        const elements = document.querySelectorAll(selector);

        elements.forEach((element, index) => {
            const mediaData = this.extractMediaData(element, index);
            this.mediaElements.push(mediaData);

            if (this.config.gallery && mediaData.gallery) {
                if (!this.galleries[mediaData.gallery]) {
                    this.galleries[mediaData.gallery] = [];
                }
                mediaData.galleryIndex = this.galleries[mediaData.gallery].length;
                this.galleries[mediaData.gallery].push(mediaData);
            }
        });

        return this.mediaElements;
    }

    /**
     * Extract media data from DOM element
     * @param {HTMLElement} element - Media link element
     * @param {number} index - Element index
     * @returns {MediaData} Media data object
     */
    extractMediaData(element, index) {
        const url = element.getAttribute('href') || element.dataset.src;
        const renderer = this.config.renderer;
        const type = element.dataset.type || renderer.detectMediaType(url);
        const parsedSize = this.parseSize(element.dataset.size);

        return {
            element,
            url,
            type,
            width: parsedSize ? parsedSize.width : null,
            height: parsedSize ? parsedSize.height : null,
            title: element.dataset.title || element.getAttribute('title') || '',
            description: element.dataset.description || '',
            gallery: element.dataset.gallery || null,
            galleryIndex: null,
            index,
            autoplay: element.dataset.autoplay === 'true' || this.config.autoplay,
            loop: element.dataset.loop === 'true' || this.config.loop,
            iframeWidth: element.dataset.iframeWidth || 853,
            iframeHeight: element.dataset.iframeHeight || 480,
            iframeAllow: element.dataset.iframeAllow || 'autoplay; fullscreen; picture-in-picture',
            theme: element.dataset.theme || null,
            size: element.dataset.size || null,
            animation: element.dataset.animation || null
        };
    }

    /**
     * Parse size string to width and height
     * @param {string|null} sizeValue - Size value in format "WIDTHxHEIGHT"
     * @returns {{width: number, height: number}|null}
     * @private
     */
    parseSize(sizeValue) {
        if (!sizeValue) {
            return null;
        }

        const [width, height] = sizeValue.split('x').map((value) => parseInt(value, 10));

        if (Number.isNaN(width) || Number.isNaN(height) || width <= 0 || height <= 0) {
            return null;
        }

        return { width, height };
    }

    /**
     * Set current gallery and index
     * @param {MediaData} mediaData - Media data
     */
    setCurrentGallery(mediaData) {
        if (mediaData.gallery && this.galleries[mediaData.gallery]) {
            this.currentGallery = mediaData.gallery;
            this.currentIndex = 'galleryIndex' in mediaData ? mediaData.galleryIndex : 0;
        } else {
            this.currentGallery = null;
            this.currentIndex = 0;
        }
    }

    /**
     * Get current media data
     * @returns {MediaData|null} Current media data
     */
    getCurrentMedia() {
        if (!this.currentGallery) return null;
        const gallery = this.galleries[this.currentGallery];
        return gallery ? gallery[this.currentIndex] : null;
    }

    /**
     * Navigate to next media
     * @returns {MediaData|null} Next media data
     */
    next() {
        if (!this.currentGallery) return null;

        const gallery = this.galleries[this.currentGallery];

        if (this.config.debugMode) {
            console.log('GalleryManager: nextMedia', {
                currentIndex: this.currentIndex,
                galleryLength: gallery.length,
                canGoNext: this.currentIndex < gallery.length - 1
            });
        }

        if (this.currentIndex < gallery.length - 1) {
            this.currentIndex++;

            if (this.config.debugMode) {
                console.log('GalleryManager: Moving to index:', this.currentIndex);
            }

            return gallery[this.currentIndex];
        }

        return null;
    }

    /**
     * Navigate to previous media
     * @returns {MediaData|null} Previous media data
     */
    prev() {
        if (!this.currentGallery) return null;

        const gallery = this.galleries[this.currentGallery];

        if (this.config.debugMode) {
            console.log('GalleryManager: prevMedia', {
                currentIndex: this.currentIndex,
                canGoPrev: this.currentIndex > 0
            });
        }

        if (this.currentIndex > 0) {
            this.currentIndex--;

            if (this.config.debugMode) {
                console.log('GalleryManager: Moving to index:', this.currentIndex);
            }

            return gallery[this.currentIndex];
        }

        return null;
    }

    /**
     * Get gallery by ID
     * @param {string} galleryId - Gallery ID
     * @returns {Array<MediaData>|null} Gallery items
     */
    getGallery(galleryId) {
        return this.galleries[galleryId] || null;
    }

    /**
     * Get all galleries
     * @returns {Object} All galleries object
     */
    getAllGalleries() {
        return this.galleries;
    }

    /**
     * Check if has gallery
     * @returns {boolean} Has active gallery
     */
    hasGallery() {
        return this.currentGallery !== null;
    }

    /**
     * Get gallery length
     * @returns {number} Gallery length or 0
     */
    getGalleryLength() {
        if (!this.currentGallery) return 0;
        const gallery = this.galleries[this.currentGallery];
        return gallery ? gallery.length : 0;
    }

    /**
     * Check if can go next
     * @returns {boolean} Can navigate to next
     */
    canGoNext() {
        if (!this.currentGallery) return false;
        const gallery = this.galleries[this.currentGallery];
        return this.currentIndex < gallery.length - 1;
    }

    /**
     * Check if can go previous
     * @returns {boolean} Can navigate to previous
     */
    canGoPrev() {
        if (!this.currentGallery) return false;
        return this.currentIndex > 0;
    }

    /**
     * Clear all data
     */
    destroy() {
        this.galleries = {};
        this.currentGallery = null;
        this.currentIndex = 0;
        this.mediaElements = [];
    }
}

export default GalleryManager;
